// Citizen Charter usage dashboard — District → Local Body → Users drill-down.
// Data: GET /api/charters/admin/usage + /api/charters/admin/local-body/users
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ChevronDown, ChevronRight, Landmark, Loader2, ScrollText, Users } from "lucide-react";

const STATUS_BADGE = {
  NOT_STARTED: "bg-slate-100 text-slate-600",
  DRAFT: "bg-slate-100 text-slate-700",
  IN_PROGRESS: "bg-amber-100 text-amber-800",
  FINAL: "bg-emerald-100 text-emerald-800",
  SUBMITTED: "bg-blue-100 text-blue-800",
  APPROVED: "bg-emerald-100 text-emerald-800",
};

function StatCard({ label, value, testid, accent }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-2xl font-black mt-1 ${accent || ""}`}>{value}</div>
    </Card>
  );
}

export default function CharterUsageTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [district, setDistrict] = useState("all");
  const [expanded, setExpanded] = useState(null); // local body key
  const [lbUsers, setLbUsers] = useState({});
  const [lbLoading, setLbLoading] = useState("");

  useEffect(() => {
    api.get("/charters/admin/usage")
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Could not load charter usage"));
  }, []);

  const toggleLb = async (row) => {
    const key = `${row.district}|${row.local_body_type}|${row.local_body_name}`;
    if (expanded === key) { setExpanded(null); return; }
    setExpanded(key);
    if (!lbUsers[key]) {
      setLbLoading(key);
      try {
        const { data: users } = await api.get("/charters/admin/local-body/users", {
          params: { district: row.district, local_body_type: row.local_body_type, local_body_name: row.local_body_name },
        });
        setLbUsers(m => ({ ...m, [key]: users }));
      } catch { setLbUsers(m => ({ ...m, [key]: [] })); }
      finally { setLbLoading(""); }
    }
  };

  if (error) {
    return <Card className="p-10 text-center text-sm text-muted-foreground" data-testid="cc-usage-error">{error}</Card>;
  }
  if (!data) {
    return (
      <Card className="p-10 flex items-center justify-center gap-2 text-muted-foreground" data-testid="cc-usage-loading">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading charter usage…
      </Card>
    );
  }

  const rows = district === "all" ? data.rows : data.rows.filter(r => r.district === district);
  const t = data.totals;

  return (
    <div className="space-y-4" data-testid="charter-usage-tab">
      <Card className="p-4 flex items-start gap-3">
        <div className="w-11 h-11 rounded-lg bg-emerald-900/10 text-emerald-800 flex items-center justify-center shrink-0">
          <ScrollText className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-bold text-lg">Citizen Charter – LSGD 2026 · Usage</h3>
          <p className="text-sm text-muted-foreground">Progress of Citizen Charter preparation across Local Bodies. Click a Local Body to see its members.</p>
        </div>
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label="Local Bodies" value={t.local_bodies} testid="cc-usage-total-lbs" />
        <StatCard label="Members" value={t.users} testid="cc-usage-total-users" />
        <StatCard label="Not Started" value={t.not_started} testid="cc-usage-not-started" accent="text-slate-600" />
        <StatCard label="In Progress" value={t.in_progress} testid="cc-usage-in-progress" accent="text-amber-600" />
        <StatCard label="Final" value={t.final} testid="cc-usage-final" accent="text-emerald-700" />
        <div className="flex items-end">
          <Select value={district} onValueChange={setDistrict}>
            <SelectTrigger data-testid="cc-usage-district-filter"><SelectValue placeholder="All districts" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All districts</SelectItem>
              {data.districts.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card data-testid="cc-usage-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead>District</TableHead>
              <TableHead>Local Body</TableHead>
              <TableHead className="text-center">Members</TableHead>
              <TableHead className="text-center">Not Started</TableHead>
              <TableHead className="text-center">In Progress</TableHead>
              <TableHead className="text-center">Final</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!rows.length && (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-sm text-muted-foreground">No local bodies found.</TableCell></TableRow>
            )}
            {rows.map(row => {
              const key = `${row.district}|${row.local_body_type}|${row.local_body_name}`;
              const open = expanded === key;
              return (
                <React.Fragment key={key}>
                  <TableRow className="cursor-pointer hover:bg-muted/40" onClick={() => toggleLb(row)} data-testid={`cc-usage-lb-${key.replace(/[^\w]+/g, "-")}`}>
                    <TableCell>{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</TableCell>
                    <TableCell className="font-medium">{row.district || "—"}</TableCell>
                    <TableCell>
                      <div className="font-semibold flex items-center gap-1.5"><Landmark className="w-3.5 h-3.5 text-emerald-800" /> {row.local_body_name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{row.local_body_type}</div>
                    </TableCell>
                    <TableCell className="text-center">{row.users}</TableCell>
                    <TableCell className="text-center">{row.not_started}</TableCell>
                    <TableCell className="text-center text-amber-700 font-semibold">{row.in_progress}</TableCell>
                    <TableCell className="text-center text-emerald-700 font-semibold">{row.final}</TableCell>
                  </TableRow>
                  {open && (
                    <TableRow className="bg-muted/20">
                      <TableCell colSpan={7} className="p-0">
                        <div className="px-8 py-3" data-testid="cc-usage-lb-users">
                          {lbLoading === key ? (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading members…</div>
                          ) : !(lbUsers[key] || []).length ? (
                            <div className="text-sm text-muted-foreground py-2">No members in this Local Body.</div>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead><Users className="w-3.5 h-3.5 inline mr-1" />Member</TableHead>
                                  <TableHead>Email</TableHead>
                                  <TableHead>Membership ID</TableHead>
                                  <TableHead>Charter Status</TableHead>
                                  <TableHead>Last Updated</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {(lbUsers[key] || []).map(u => (
                                  <TableRow key={u.user_id} data-testid={`cc-usage-user-${u.user_id}`}>
                                    <TableCell className="font-medium">{u.name || "—"}</TableCell>
                                    <TableCell className="text-xs">{u.email}</TableCell>
                                    <TableCell className="text-xs font-mono">{u.membership_id || "—"}</TableCell>
                                    <TableCell>
                                      <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${STATUS_BADGE[u.charter_status] || STATUS_BADGE.NOT_STARTED}`}>
                                        {(u.charter_status || "NOT_STARTED").replace(/_/g, " ")}
                                      </span>
                                    </TableCell>
                                    <TableCell className="text-xs">{u.charter_updated_at ? new Date(u.charter_updated_at).toLocaleString() : "—"}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
